import { useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { SubmitForm } from "@/components/submit/SubmitForm";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { BoardDetail } from "@/lib/types";

/**
 * Manual ticket creation by a department member. A lead opens this from a ticket
 * board's toolbar to file a ticket on behalf of a requester who cannot write a
 * message themselves — documenting the case cleanly. Unlike SubmitTicketDialog
 * (public /submit, fetches submit-info), this reuses the already-loaded board
 * detail and posts to the member-gated `/tickets` endpoint via SubmitForm's
 * `internal` variant, which surfaces optional fields (e.g. Priorität) too.
 */
export function NewTicketDialog({
  open,
  onOpenChange,
  board,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  board: BoardDetail | null;
}) {
  const { user } = useAuth();
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Radix calls onOpenChange(false) on backdrop-click + Esc. If the user has
  // already filled something in, keep the dialog open and ask for confirmation
  // (styled, not window.confirm) before discarding the work.
  function requestClose(next: boolean) {
    if (!next && dirty) {
      setConfirmOpen(true);
      return;
    }
    setDirty(false);
    onOpenChange(next);
  }

  function discardAndClose() {
    setConfirmOpen(false);
    setDirty(false);
    onOpenChange(false);
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={requestClose}
        title={t.newTicketDialogTitle}
        description={
          board ? `${t.newTicketDialogDescription} · ${board.name}` : t.newTicketDialogDescription
        }
        width={560}
      >
        {board ? (
          <SubmitForm
            boardId={board.id}
            detail={board}
            currentUserSub={user?.sub ?? null}
            variant="internal"
            submitFn={api.createTicket}
            submitLabel={t.newTicketButton}
            onDirtyChange={setDirty}
            onSubmitted={() => {
              showToast(t.newTicketCreated, "success");
              setDirty(false);
              onOpenChange(false);
            }}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t.discardTitle}
        message={t.discardMessage}
        confirmLabel={t.discardConfirm}
        cancelLabel={t.discardCancel}
        tone="danger"
        onConfirm={discardAndClose}
      />
    </>
  );
}
